import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";

import { type Collaborators, type PublicAccess, type SlackChannel } from "@doitintl/cmp-models";
import { Box, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cloneDeep from "lodash/cloneDeep";
import concat from "lodash/concat";
import differenceWith from "lodash/differenceWith";
import isEqual from "lodash/isEqual";
import uniq from "lodash/uniq";
import { DateTime } from "luxon";

import { budgetText, globalText } from "../../../../assets/texts";
import { budgetTxt } from "../../../../assets/texts/CloudAnalytics/budget";
import { useUserContext } from "../../../../Context/UserContext";
import { type BudgetAlert, type BudgetInfo, type DraftBudget } from "../../../../types";
import { getCurrencySymbol, onKeyPressPreventNonNumeric } from "../../../../utils/common";
import { dateFormatFullDateWithDay } from "../../../../utils/dateTimeFormats";
import ShareDialog from "../../dialogs/shareDialog/ShareDialog";
import { type CurrentLastPeriods } from "../../ReportData";
import { checkAllowedSubdomains, CloudAnalyticsEntities } from "../../utilities";
import { getAlertAmountFromPercentage, textFieldBaseProps } from "../shared";
import NotificationSettings from "./NotificationSettings";

type Props = {
  budgetInfo: BudgetInfo;
  setBudgetInfo: Dispatch<SetStateAction<BudgetInfo>>;
  isCurrentUserEditor: boolean;
  budget: DraftBudget;
  shouldRefreshData: boolean;
  recipients: string[];
  recipientsSlackChannels: SlackChannel[];
  setRecipients: Dispatch<SetStateAction<string[]>>;
  setRecipientsSlackChannels: Dispatch<SetStateAction<SlackChannel[]>>;
  isNewBudget: boolean;
  currentLastPeriods: CurrentLastPeriods;
  thresholdsAmount: number;
  setPublicAccess: Dispatch<SetStateAction<PublicAccess | null>>;
  setNewRecipientsSlackChannels: Dispatch<SetStateAction<SlackChannel[]>>;
};

const Step3 = ({
  budgetInfo,
  setBudgetInfo,
  isCurrentUserEditor,
  budget,
  shouldRefreshData,
  recipients,
  recipientsSlackChannels,
  setRecipients,
  setRecipientsSlackChannels,
  isNewBudget,
  currentLastPeriods,
  thresholdsAmount,
  setPublicAccess,
  setNewRecipientsSlackChannels,
}: Props) => {
  const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
  const [shareLoading, setShareLoading] = useState<boolean>(false);
  const { userRoles } = useUserContext({ requiredRoles: true });

  const isCurrentUserUserManager = userRoles.usersManager || userRoles.doitEmployee;

  const renderForecastedDate = useCallback(
    (alert: BudgetAlert) => {
      const loading = false;
      const alertAmount = alert.amount ?? 0;
      const currentPeriodAmount = currentLastPeriods?.currentPeriod ?? 0;

      if (alert.amount && loading) {
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {globalText.LOADING}
          </Typography>
        );
      }

      if (alertAmount > 0 && currentPeriodAmount > 0 && alertAmount <= currentPeriodAmount) {
        return (
          <Typography color="error" variant="body2">
            {budgetTxt.CREATE_BUDGET.STEP_3.ALERT_THRESHOLD_CROSSED}
            {budgetInfo.currentTypeAndFrequency.period}
          </Typography>
        );
      }

      const forecastedDate = alert.forecastedDate;

      if (forecastedDate) {
        return (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Forecasted Date:
            <Typography
              component="span"
              variant="body2"
              sx={{
                ml: 0.5,
                fontWeight: 700,
              }}
            >
              {DateTime.fromMillis(forecastedDate.seconds * 1000).toFormat(dateFormatFullDateWithDay)}
            </Typography>
          </Typography>
        );
      } else if (shouldRefreshData) {
        return (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
            }}
          >
            {globalText.NA}
          </Typography>
        );
      } else if (alert.amount) {
        return (
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              lineHeight: "20px",
            }}
          >
            {budgetText.ALERT_THRESHOLD_NO_REACH}
          </Typography>
        );
      }
      return (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
          }}
        >
          {globalText.NA}
        </Typography>
      );
    },
    [budgetInfo.currentTypeAndFrequency.period, currentLastPeriods, shouldRefreshData]
  );

  const getAlertPercentageFromAmount = useCallback(
    (alertAmount: number) => {
      if (!alertAmount) {
        return 0;
      }
      return (alertAmount / thresholdsAmount) * 100;
    },
    [thresholdsAmount]
  );

  const handleAlertChange = useCallback(
    (fieldName: string, value, index: number) => {
      setBudgetInfo((prev) => {
        const newAlert: BudgetAlert = {
          amount: 0,
          percentage: 0,
          triggered: false,
        };
        switch (fieldName) {
          case "percentage":
            newAlert.percentage = value ? parseInt(value) : 0;
            newAlert.amount = getAlertAmountFromPercentage(value ? parseInt(value) : 0, thresholdsAmount);
            break;
          case "amount":
            newAlert.amount = value ? parseInt(value) : 0;
            newAlert.percentage = getAlertPercentageFromAmount(value ? parseInt(value) : 0);
        }
        const newAlerts = cloneDeep(prev.alerts);
        newAlerts[index] = newAlert;

        return {
          ...prev,
          alerts: newAlerts,
        };
      });
    },
    [thresholdsAmount, getAlertPercentageFromAmount, setBudgetInfo]
  );

  const handleOnBlur = useCallback(() => {
    setBudgetInfo((prev) => {
      const zeros = prev.alerts.filter((a) => a.percentage === 0);
      const sortedNonZeros = prev.alerts.filter((a) => a.percentage !== 0).sort((a, b) => a.percentage - b.percentage);
      const alerts = [...sortedNonZeros, ...zeros];

      return {
        ...prev,
        alerts,
      };
    });
  }, [setBudgetInfo]);

  const handleChangeSharing = useCallback(
    async (
      collaborators: Collaborators,
      publicAccess: PublicAccess,
      newRecipients: string[] = recipients,
      newRecipientsSlackChannels: SlackChannel[] = recipientsSlackChannels
    ) => {
      if (isNewBudget) {
        return;
      }

      setShareLoading(true);
      if (isCurrentUserUserManager) {
        setBudgetInfo((prev) => {
          const temp = { ...prev };
          temp.collaborators = collaborators;
          return temp;
        });
      }
      const verifiedRecipients = newRecipients.filter(
        (r) => checkAllowedSubdomains(r) || collaborators.find((c) => c.email === r)
      );
      const newCollabs = differenceWith(collaborators, budget.data.collaborators, isEqual);
      const recipientsWithNewCollaborators = uniq(
        concat(
          verifiedRecipients,
          newCollabs.map((r) => r.email)
        )
      );
      setPublicAccess(publicAccess);
      setNewRecipientsSlackChannels(newRecipientsSlackChannels);

      setShareDialogOpen(false);
      setRecipients(recipientsWithNewCollaborators);

      setShareLoading(false);
    },
    [
      budget,
      isCurrentUserUserManager,
      isNewBudget,
      recipients,
      recipientsSlackChannels,
      setBudgetInfo,
      setNewRecipientsSlackChannels,
      setPublicAccess,
      setRecipients,
    ]
  );

  const newCollaborators = useMemo(
    () => recipients.filter((r) => !budget.data.collaborators.find((c) => c.email === r)),
    [recipients, budget.data.collaborators]
  );

  const alertItem = (alert: BudgetAlert, i: number) => (
    <Grid
      container
      spacing={1}
      sx={{
        mb: 2,
      }}
    >
      <Grid size={6}>
        <TextField
          label={budgetTxt.CREATE_BUDGET.STEP_3.PERCENTAGE}
          type="number"
          onKeyUp={onKeyPressPreventNonNumeric}
          onBlur={handleOnBlur}
          {...textFieldBaseProps}
          value={alert.percentage || ""}
          onChange={(event) => {
            handleAlertChange("percentage", event.target.value, i);
          }}
          disabled={!thresholdsAmount || !isCurrentUserEditor}
          data-testid="percentage"
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">%</InputAdornment>,
            },

            htmlInput: { min: 0 },
          }}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          label={budgetTxt.CREATE_BUDGET.STEP_3.BUDGET_AMOUNT}
          type="number"
          onKeyUp={onKeyPressPreventNonNumeric}
          onBlur={handleOnBlur}
          {...textFieldBaseProps}
          value={alert.amount || ""}
          onChange={(event) => {
            handleAlertChange("amount", event.target.value, i);
          }}
          disabled={!thresholdsAmount || !isCurrentUserEditor}
          data-testid="amount"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">{getCurrencySymbol(budgetInfo.currency)}</InputAdornment>
              ),
            },

            htmlInput: { min: 0 },
          }}
        />
        {renderForecastedDate(alert)}
      </Grid>
    </Grid>
  );
  return (
    <Stack
      sx={{
        mt: 4,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 500,
          mb: 2,
        }}
      >
        {budgetTxt.CREATE_BUDGET.STEP_3.TITLE}
      </Typography>
      <Box>{budgetInfo.alerts.map((alert, i) => alertItem(alert, i))}</Box>
      <NotificationSettings
        isCurrentUserEditor={isCurrentUserEditor}
        setShareDialogOpen={setShareDialogOpen}
        budget={budget}
        setRecipients={setRecipients}
        setRecipientsSlackChannels={setRecipientsSlackChannels}
        recipients={recipients}
        recipientsSlackChannels={recipientsSlackChannels}
        handleChangeSharing={handleChangeSharing}
        shareLoading={shareLoading}
        isCurrentUserUserManager={isCurrentUserUserManager}
      />
      {!isNewBudget && !!budget.data.collaborators.length && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => {
            setShareDialogOpen(false);
            setRecipients(budget.data.recipients);
          }}
          entity={CloudAnalyticsEntities.BUDGET}
          onSharingChanged={handleChangeSharing}
          predefinedCollaborators={newCollaborators}
          loading={shareLoading}
          shareEntities={[budget.data]}
        />
      )}
    </Stack>
  );
};

export default Step3;
